import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, API_BASE } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { TableLoading } from '@/components/ui/loading';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { permissionSignature } from '@/lib/permissions';
import { ChevronDown, ChevronUp, ChevronsUpDown, Download, Loader2, RotateCcw } from 'lucide-react';
import { validateDateRange } from '@/lib/validation';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import type {
  ProjectSummaryRow,
  CategoryDetailRow,
  CategoryDetailTotals,
  BudgetActualRow,
  MultiSummaryRow,
  MultiSummaryTotals,
} from '@/lib/types';
import './print-report.css';

type ReportType = 'summary' | 'category' | 'budget' | 'multi';

interface ReportConfig {
  value: ReportType;
  label: string;
  endpoint: string;
  title: string;
  description: string;
  requiresProject: boolean;
  allowsDimensions: boolean;
  allowsMultiProjects?: boolean;
}

const REPORT_TYPES: ReportConfig[] = [
  {
    value: 'summary',
    label: 'Project Specific Summary',
    endpoint: 'project-summary',
    title: 'Project Specific Summary',
    description: 'Departmental Labour-Requirement vs Departmental Labour -Actual and cost for the selected project.',
    requiresProject: true,
    allowsDimensions: true,
  },
  {
    value: 'category',
    label: 'Project Specific Category Detail',
    endpoint: 'category-detail',
    title: 'Project Specific Category Detail',
    description: 'Departmental Labour-Requirement vs Departmental Labour -Actual and cost broken down by worker category for the selected project.',
    requiresProject: true,
    allowsDimensions: true,
  },
  {
    value: 'budget',
    label: 'All Projects Budget vs Actual',
    endpoint: 'budget-actual',
    title: 'All Projects Budget vs Actual',
    description: 'Departmental Labour-Requirement vs Departmental Labour -Actual and cost across all active projects, broken down by block, activity and worker category.',
    requiresProject: false,
    allowsDimensions: true,
  },
{
    value: 'multi',
    label: 'Multi Project Summary',
    endpoint: 'multi-summary',
    title: 'Multi Project Summary',
    description: 'One row per selected active project that has approved Departmental Labour-Requirement or Departmental Labour -Actual in the selected period.',
    requiresProject: false,
    allowsDimensions: false,
allowsMultiProjects: true,
  },
];

interface ReportFilters {
  reportType: ReportType;
  projectId: string;
  projectIds: string[];
  dateFrom: string;
  dateTo: string;
  blockId: string;
  activityId: string;
  workerCategoryId: string;
}

const DEFAULT_FILTERS: ReportFilters = {
  reportType: 'summary',
  projectId: '',
  projectIds: [],
  dateFrom: '',
  dateTo: '',
  blockId: '',
  activityId: '',
  workerCategoryId: '',
};

const DEFAULT_SORTS: Record<ReportType, { key: string; dir: 'asc' | 'desc' }> = {
  summary: { key: '', dir: 'asc' },
  category: { key: 'category_name', dir: 'asc' },
  budget: { key: 'project_name', dir: 'asc' },
  multi: { key: 'project_name', dir: 'asc' },
}

const PAGE_SIZE = 20;

function formatPercentMagnitude(value: number): string {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
}

function getBudgetVarianceStatus(
  plannedAmount: number,
  actualAmount: number,
): 'Under Budget' | 'Over Budget' | 'On Budget' {
  if (actualAmount < plannedAmount) return 'Under Budget';
  if (actualAmount > plannedAmount) return 'Over Budget';
  return 'On Budget';
}

function getLabourVarianceStatus(
  required: number,
  actual: number,
): 'Under Plan' | 'Over Plan' | 'On Plan' {
  if (actual < required) return 'Under Plan';
  if (actual > required) return 'Over Plan';
  return 'On Plan';
}

function labourVarianceLabel(variance: number, required: number, actual: number): string {
  const magnitude = Math.abs(variance);
  if (variance === 0) return 'On Plan';
  if (variance < 0) return `${formatNumber(magnitude)} Under Plan`;
  return `${formatNumber(magnitude)} Over Plan`;
}

function varianceAmountLabel(amountVariance: number, plannedAmount: number, actualAmount: number): string {
  const absoluteDifference = Math.abs(amountVariance);
  const status = getBudgetVarianceStatus(plannedAmount, actualAmount);
  return `${formatCurrency(absoluteDifference)} ${status}`;
}

function variancePercentLabel(
  amountVariance: number,
  plannedAmount: number,
  actualAmount: number,
  variancePercentage: number | null,
): string {
  const status = getBudgetVarianceStatus(plannedAmount, actualAmount);
  if (plannedAmount === 0) {
    return actualAmount === 0 ? '0% On Budget' : `N/A — ${status}`;
  }
  if (variancePercentage === null) {
    return actualAmount === 0 ? '0% On Budget' : `N/A — ${status}`;
  }
  if (amountVariance === 0) return '0% On Budget';
  return `${formatPercentMagnitude(variancePercentage)}% ${status}`;
}

function varianceClass(variance: number): string {
  if (variance > 0) return 'text-red-600';
  if (variance < 0) return 'text-emerald-600';
  return 'text-blue-600';
}

function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  return value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function emDashIfNull(value: string | null | undefined): string {
  return value ? value : '—';
}

interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

function sortRows<T>(rows: T[], sort: SortState): T[] {
  if (!sort.key) return rows;
  const mult = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as any)[sort.key];
    const bv = (b as any)[sort.key];
    if (av === null || av === undefined || av === '') return mult;
    if (bv === null || bv === undefined || bv === '') return -mult;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
    return String(av).localeCompare(String(bv)) * mult;
  });
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: string;
  sort: SortState;
  onSort: (key: string) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return (
    <TableHead className={align === 'right' ? 'text-right' : ''}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 font-medium uppercase tracking-wide text-secondary hover:text-primary ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
        ) : (
          <ChevronsUpDown size={12} />
        )}
      </button>
    </TableHead>
  );
}

function ErrorState({ onRetry, message }: { onRetry: () => void; message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <p className="text-sm text-red-500">{message || 'Unable to load the report. Please try again.'}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <div className="py-10 text-center text-sm text-muted-foreground">{message}</div>;
}

interface DetailedQuery<T> {
  data?: T;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

function StatusBadge({ text, variant }: { text: string; variant: 'success' | 'danger' | 'default' }) {
  const classes =
    variant === 'success'
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : variant === 'danger'
        ? 'bg-red-50 text-red-700 border border-red-200'
        : 'bg-blue-50 text-blue-700 border border-blue-200';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}>{text}</span>;
}

function labourVarianceBadge(variance: number, required: number, actual: number) {
  if (variance === 0) return <StatusBadge text="On Plan" variant="default" />;
  if (variance < 0) return <StatusBadge text={`${Math.abs(variance)} Under Plan`} variant="success" />;
  return <StatusBadge text={`${variance} Over Plan`} variant="danger" />;
}

function amountVarianceBadge(amountVariance: number, plannedAmount: number, actualAmount: number) {
  const status = getBudgetVarianceStatus(plannedAmount, actualAmount);
  const abs = Math.abs(amountVariance);
  if (status === 'On Budget') return <StatusBadge text="On Budget" variant="default" />;
  if (status === 'Under Budget') return <StatusBadge text={`${formatCurrency(abs)} Under Budget`} variant="success" />;
  return <StatusBadge text={`${formatCurrency(abs)} Over Budget`} variant="danger" />;
}

function percentVarianceBadge(
  amountVariance: number,
  plannedAmount: number,
  actualAmount: number,
  variancePercentage: number | null,
) {
  const status = getBudgetVarianceStatus(plannedAmount, actualAmount);
  if (plannedAmount === 0) {
    return actualAmount === 0
      ? <StatusBadge text="0% On Budget" variant="default" />
      : <StatusBadge text={`N/A — ${status}`} variant="danger" />;
  }
  if (variancePercentage === null) {
    return actualAmount === 0
      ? <StatusBadge text="0% On Budget" variant="default" />
      : <StatusBadge text={`N/A — ${status}`} variant="danger" />;
  }
  if (amountVariance === 0) return <StatusBadge text="0% On Budget" variant="default" />;
  const text = `${formatPercentMagnitude(variancePercentage)}% ${status}`;
  if (status === 'Under Budget') return <StatusBadge text={text} variant="success" />;
  return <StatusBadge text={text} variant="danger" />;
}

export default function ReportsPage() {
  const { user, hasPermission } = useAuth();
  const userId = user?.id ?? 'anonymous';
  const permSig = permissionSignature(user?.permissions ?? []);
  const canExport = hasPermission('reports:export');
  const { addToast } = useToast();

  const [filters, setFilters] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [draft, setDraft] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORTS.summary);
  const [exporting, setExporting] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [removalMessage, setRemovalMessage] = useState<string | null>(null);

  const activeReport = REPORT_TYPES.find((r) => r.value === filters.reportType) ?? REPORT_TYPES[0];
  const draftReport = REPORT_TYPES.find((r) => r.value === draft.reportType) ?? REPORT_TYPES[0];

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    setSort(DEFAULT_SORTS[filters.reportType]);
  }, [filters.reportType]);

  const draftDateResult = validateDateRange(draft.dateFrom, draft.dateTo);
  const appliedDateResult = validateDateRange(filters.dateFrom, filters.dateTo);
  const draftInvalid = !draftDateResult.valid || (draftReport.requiresProject && !draft.projectId);
const projectMissing = activeReport.requiresProject && !filters.projectId;
  const multiMissing = !!activeReport.allowsMultiProjects && filters.projectIds.length === 0;
  const filtersReady = appliedDateResult.valid && !projectMissing && !multiMissing;

  const buildParams = (f: ReportFilters) => {
    const p = new URLSearchParams();
    if (f.dateFrom) p.set('dateFrom', f.dateFrom);
    if (f.dateTo) p.set('dateTo', f.dateTo);
    const cfg = REPORT_TYPES.find((r) => r.value === f.reportType) ?? REPORT_TYPES[0];
    if (cfg.requiresProject && f.projectId) p.set('projectId', f.projectId);
    if (cfg.allowsMultiProjects) {
      for (const id of f.projectIds) p.append('projectIds', id);
    }
    if (cfg.allowsDimensions) {
      if (f.blockId) p.set('blockId', f.blockId);
      if (f.activityId) p.set('activityId', f.activityId);
      if (f.workerCategoryId) p.set('workerCategoryId', f.workerCategoryId);
    }
    return p.toString();
  };

  const applyFilters = () => {
    if (draftReport.allowsMultiProjects && draft.projectIds.length === 0) {
      setSelectionError('Select at least one active project.');
      return;
    }
    setSelectionError(null);
    setFilters(draft);
    setPage(1);
  };

  const resetFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setSelectionError(null);
    setRemovalMessage(null);
  };

  const onDraftProjectChange = (value: string) => {
    setDraft((d) => ({ ...d, projectId: value, blockId: '', activityId: '' }));
  };

  const onMultiProjectsChange = (next: string[]) => {
    setDraft((d) => ({ ...d, projectIds: next }));
    if (next.length > 0) setSelectionError(null);
    setRemovalMessage(null);
  };

  const handleRemovedProjects = (removedIds: string[]) => {
    if (!removedIds || removedIds.length === 0) return;
    setRemovalMessage('One or more unavailable projects were removed from the selection.');
    setDraft((d) => ({ ...d, projectIds: d.projectIds.filter((id) => !removedIds.includes(id)) }));
    setFilters((f) => ({ ...f, projectIds: f.projectIds.filter((id) => !removedIds.includes(id)) }));
  };

  const onSortChange = (key: string) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const { data: projects } = useQuery({
    queryKey: ['projects-list'],
    queryFn: async () => {
      const r = await api.get<{ data: any[] }>('/projects?availability=active');
      return r.data ?? [];
    },
  });

  const {
    data: activeProjects,
    isLoading: activeProjectsLoading,
    isError: activeProjectsError,
    refetch: refetchActiveProjects,
  } = useQuery({
    queryKey: ['report-active-projects'],
    queryFn: async () => {
      const r = await api.get<{ data: any[] }>('/projects?availability=active&limit=1000');
      return r.data ?? [];
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const activeProjectOptions = (activeProjects ?? []).map((p) => ({
    value: p.id ?? p.project_id,
    label: p.name ?? p.project_name,
  }));

  const { data: blocks } = useQuery({
    queryKey: ['report-blocks', draft.projectId],
    queryFn: async () => {
      const q = draft.projectId ? `?projectId=${encodeURIComponent(draft.projectId)}` : '';
      const r = await api.get<{ data: any[] }>(`/blocks/all${q}`);
      return r.data ?? [];
    },
    enabled: draftReport.allowsDimensions,
  });

  const { data: activities } = useQuery({
    queryKey: ['report-activities', draft.projectId, draft.blockId],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (draft.projectId) q.set('projectId', draft.projectId);
      if (draft.blockId) q.set('blockId', draft.blockId);
      const r = await api.get<{ data: any[] }>(`/activities/all?${q.toString()}`);
      return r.data ?? [];
    },
    enabled: draftReport.allowsDimensions,
  });

  const { data: workerCategories } = useQuery({
    queryKey: ['labour-categories'],
    queryFn: async () => {
      const r = await api.get<{ data: any[] }>('/labour-categories');
      return r.data ?? [];
    },
    enabled: draftReport.allowsDimensions,
  });

  const summaryQuery = useQuery({
    queryKey: ['report-project-summary', userId, permSig, filters],
    queryFn: async () => {
      const r = await api.get<{ data: ProjectSummaryRow }>(`/reports/project-summary?${buildParams(filters)}`);
      return r.data;
    },
    enabled: filtersReady && filters.reportType === 'summary',
  });

  const categoryQuery = useQuery({
    queryKey: ['report-category-detail', userId, permSig, filters],
    queryFn: async () => {
      const r = await api.get<{ data: { rows: CategoryDetailRow[]; totals: CategoryDetailTotals } }>(
        `/reports/category-detail?${buildParams(filters)}`
      );
      return r.data;
    },
    enabled: filtersReady && filters.reportType === 'category',
  });

  const budgetQuery = useQuery({
    queryKey: ['report-budget-actual', userId, permSig, filters],
    queryFn: async () => {
      const r = await api.get<{ data: { rows: BudgetActualRow[] } }>(`/reports/budget-actual?${buildParams(filters)}`);
      return r.data;
    },
    enabled: filtersReady && filters.reportType === 'budget',
  });

const multiQuery = useQuery({
    queryKey: ['report-multi-summary', userId, permSig, filters],
    queryFn: async () => {
      const r = await api.get<{
        data: { rows: MultiSummaryRow[]; totals: MultiSummaryTotals };
        removedProjectIds?: string[];
      }>(`/reports/multi-summary?${buildParams(filters)}`);
      if (r.removedProjectIds?.length) handleRemovedProjects(r.removedProjectIds);
      return r.data;
    },
    enabled: filtersReady && filters.reportType === 'multi' && filters.projectIds.length > 0,
  });

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const query = new URLSearchParams();
      if (filters.dateFrom) query.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) query.set('dateTo', filters.dateTo);
      if (activeReport.requiresProject && filters.projectId) query.set('projectId', filters.projectId);
      if (activeReport.allowsMultiProjects) {
        for (const id of filters.projectIds) query.append('projectIds', id);
      }
      if (activeReport.allowsDimensions) {
        if (filters.blockId) query.set('blockId', filters.blockId);
        if (filters.activityId) query.set('activityId', filters.activityId);
        if (filters.workerCategoryId) query.set('workerCategoryId', filters.workerCategoryId);
      }
      query.set('format', 'csv');

      const token = sessionStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API_BASE}/reports/${activeReport.endpoint}?${query.toString()}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (response.status === 401) {
        sessionStorage.removeItem('access_token');
        throw new Error('Your session has expired. Please sign in again.');
      }

      if (!response.ok) {
        let msg = `Unable to export report (${response.status})`;
        try {
          const body = await response.json();
          msg = body.error || body.message || msg;
        } catch { /* ignore */ }
        throw new Error(msg);
      }

      const contentType = response.headers.get('Content-Type') || '';
      if (!contentType.includes('text/csv')) {
        throw new Error('The server did not return a CSV file.');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match?.[1] || `${activeReport.endpoint}.csv`;
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);

      addToast('Report downloaded successfully', 'success');
    } catch (error: any) {
      addToast(error.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const blockLabel = (b: any) => (filters.projectId ? b.name : b.project_name ? `${b.name} (${b.project_name})` : b.name);
  const activityLabel = (a: any) => {
    const parts = [a.name];
    if (!filters.projectId && a.block_name) parts.push(a.block_name);
    if (!filters.projectId && a.project_name) parts.push(a.project_name);
    return parts.join(' — ');
  };

  const validationMessage = !draftDateResult.valid
    ? draftDateResult.startError || draftDateResult.endError
    : !appliedDateResult.valid
      ? appliedDateResult.startError || appliedDateResult.endError
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary">Reports</h1>
          <p className="text-sm text-muted-foreground">Compare Departmental Labour-Requirement and Departmental Labour -Actual across projects</p>
        </div>
<div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={downloadCsv}
            disabled={exporting || !filtersReady || !canExport}
            aria-label="Export CSV"
          >
            {exporting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="report-type">Report Type</Label>
              <Select
                value={draft.reportType}
                onValueChange={(v) => setDraft((d) => ({ ...d, reportType: v as ReportType }))}
              >
                <SelectTrigger id="report-type" className="w-full"><SelectValue placeholder="Report Type" /></SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {draftReport.requiresProject && (
              <div className="grid gap-1.5">
                <Label htmlFor="report-project">Project</Label>
                <Select value={draft.projectId} onValueChange={onDraftProjectChange}>
                  <SelectTrigger id="report-project" className="w-full"><SelectValue placeholder="Select a project" /></SelectTrigger>
                  <SelectContent>
                    {projects?.map((p) => (
                      <SelectItem key={p.id ?? p.project_id} value={p.id ?? p.project_id}>{p.name ?? p.project_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {draftReport.allowsMultiProjects && (
              <div className="grid gap-1.5">
                <Label htmlFor="report-multi-projects">Projects *</Label>
                <MultiSelect
                  id="report-multi-projects"
                  value={draft.projectIds}
                  onValueChange={onMultiProjectsChange}
                  options={activeProjectOptions}
                  isLoading={activeProjectsLoading}
                  isError={activeProjectsError}
                  onRetry={() => refetchActiveProjects()}
                  onOpenChange={(open) => { if (open) refetchActiveProjects(); }}
                  searchPlaceholder="Search active projects..."
                  emptyMessage="No active projects available."
                  ariaLabel="Projects"
                />
                {selectionError && (
                  <p className="text-xs text-red-500" role="alert">{selectionError}</p>
                )}
                {removalMessage && (
                  <p className="text-xs text-amber-600" role="status">{removalMessage}</p>
                )}
              </div>
            )}

<div className="grid gap-1.5">
              <Label htmlFor="report-from">Start Date</Label>
              <Input id="report-from" type="date" value={draft.dateFrom} onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, dateFrom: v, dateTo: v })); }} className="w-full" placeholder="From" />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="report-to">End Date</Label>
              <Input id="report-to" type="date" value={draft.dateTo} onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, dateFrom: v, dateTo: v })); }} className="w-full" placeholder="To" />
            </div>

            {draftReport.allowsDimensions && (
              <>
                <div className="grid gap-1.5">
                  <Label htmlFor="report-block">Block</Label>
                  <Select value={draft.blockId} onValueChange={(v) => setDraft((d) => ({ ...d, blockId: v, activityId: '' }))}>
                    <SelectTrigger id="report-block" className="w-full"><SelectValue placeholder="All Blocks" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Blocks</SelectItem>
                      {blocks?.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{blockLabel(b)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="report-activity">Activity</Label>
                  <Select value={draft.activityId} onValueChange={(v) => setDraft((d) => ({ ...d, activityId: v }))}>
                    <SelectTrigger id="report-activity" className="w-full"><SelectValue placeholder="All Activities" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Activities</SelectItem>
                      {activities?.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{activityLabel(a)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="report-category">Worker Category</Label>
                  <Select value={draft.workerCategoryId} onValueChange={(v) => setDraft((d) => ({ ...d, workerCategoryId: v }))}>
                    <SelectTrigger id="report-category" className="w-full"><SelectValue placeholder="All Worker Categories" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Worker Categories</SelectItem>
                      {workerCategories?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="grid gap-1.5">
              <div className="flex items-center gap-2">
                <Button onClick={applyFilters} disabled={draftInvalid || (!!draftReport.allowsMultiProjects && activeProjectsLoading)}>Apply</Button>
                <Button variant="outline" size="icon" onClick={resetFilters} aria-label="Reset filters" title="Reset filters">
                  <RotateCcw size={16} />
                </Button>
              </div>
            </div>
          </div>

          {validationMessage && (
            <p className="mt-3 text-xs text-red-500" role="alert">{validationMessage}</p>
          )}
        </CardContent>
      </Card>

      {!filtersReady ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{activeReport.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <EmptyState message={projectMissing ? 'Select a project to view this report.' : multiMissing ? 'Select at least one active project to view this report.' : 'Correct the date range to view this report.'} />
          </CardContent>
        </Card>
      ) : (
        <>
          {filters.reportType === 'summary' && (
            <SummaryCard query={summaryQuery} sort={sort} onSort={onSortChange} />
          )}
          {filters.reportType === 'category' && (
            <CategoryCard query={categoryQuery} sort={sort} onSort={onSortChange} page={page} onPageChange={setPage} />
          )}
          {filters.reportType === 'budget' && (
            <BudgetCard query={budgetQuery} sort={sort} onSort={onSortChange} page={page} onPageChange={setPage} />
          )}
{filters.reportType === 'multi' && (
            <MultiCard
              query={multiQuery}
              sort={sort}
              onSort={onSortChange}
              page={page}
              onPageChange={setPage}
              selectedProjectIds={filters.projectIds}
            />
          )}
        </>
      )}
    </div>
  );
}

function SummaryCards({ row }: { row: ProjectSummaryRow }) {
  const required = row.planned_manpower;
  const plannedUsed = row.actual_regular_manpower;
  const additionalUsed = row.actual_adhoc_manpower;
  const totalActual = row.total_actual_manpower;
  const labourVariance = row.manpower_variance;
  const status = getLabourVarianceStatus(required, totalActual);

  const cards = [
    { label: 'Required Labour', value: formatNumber(required), color: 'text-primary' },
    { label: 'Planned Labour Used', value: formatNumber(plannedUsed), color: 'text-primary' },
    { label: 'Additional Labour Used', value: formatNumber(additionalUsed), color: 'text-primary' },
    { label: 'Total Departmental Labour -Actual', value: formatNumber(totalActual), color: 'text-primary' },
    {
      label: 'Labour Variance',
      value: labourVariance === 0 ? 'On Plan' : `${Math.abs(labourVariance)} ${status}`,
      color: labourVariance === 0 ? 'text-blue-600' : labourVariance < 0 ? 'text-emerald-600' : 'text-red-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map((c) => (
        <Card key={c.label}>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground truncate">{c.label}</p>
            <p className={`text-lg font-semibold mt-1 ${c.color}`}>{c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function LabourComparisonChart({
  data,
  chartData,
}: {
  data: { project_name: string; planned_manpower: number; total_actual_manpower: number }[];
  chartData: { name: string; 'Required Labour': number; 'Total Actual Labour': number }[];
}) {
  if (chartData.length === 0) return null;
  const height = Math.max(120, Math.min(300, 60 + chartData.length * 36));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Labour Comparison</CardTitle>
        <p className="text-xs text-muted-foreground">Required vs total actual</p>
      </CardHeader>
      <CardContent>
        <div role="img" aria-label={`Labour comparison. ${data.map((d) => `${d.project_name}: required ${d.planned_manpower}, actual ${d.total_actual_manpower}`).join('. ')}`}>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
              <RechartsTooltip formatter={(v: number) => formatNumber(v)} />
              <Legend />
              <Bar dataKey="Required Labour" fill="#3b5998" radius={[0, 4, 4, 0]} />
              <Bar dataKey="Total Actual Labour" fill="#10b981" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function AmountSummaryCard({
  plannedAmount,
  actualAmount,
  amountVariance,
  variancePercentage,
}: {
  plannedAmount: number;
  actualAmount: number;
  amountVariance: number;
  variancePercentage: number | null;
}) {
  const status = getBudgetVarianceStatus(plannedAmount, actualAmount);
  const abs = Math.abs(amountVariance);

  let percentText = '';
  if (plannedAmount > 0 && variancePercentage !== null) {
    percentText = `${formatPercentMagnitude(variancePercentage)}% ${status}`;
  } else if (plannedAmount === 0 && actualAmount === 0) {
    percentText = '0% On Budget';
  } else {
    percentText = `N/A — ${status}`;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Amount Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Planned Amount</span>
          <span className="text-sm font-semibold">{formatCurrency(plannedAmount)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Actual Amount</span>
          <span className="text-sm font-semibold">{formatCurrency(actualAmount)}</span>
        </div>
        <div className="border-t pt-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Amount Variance</span>
            <span className={`text-sm font-semibold ${varianceClass(amountVariance)}`}>{formatCurrency(abs)}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-muted-foreground">Status</span>
            <span className="text-sm font-semibold">{percentText}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryCard({
  query,
  sort,
  onSort,
}: {
  query: { data?: ProjectSummaryRow; isLoading: boolean; isError: boolean; refetch: () => void };
  sort: SortState;
  onSort: (key: string) => void;
}) {
  const row = query.data;

  if (query.isLoading) {
    return <Card><CardContent><TableLoading rows={3} /></CardContent></Card>;
  }
  if (query.isError) {
    return <Card><CardContent><ErrorState onRetry={query.refetch} /></CardContent></Card>;
  }
  if (!row) {
    return <Card><CardContent><EmptyState message="No approved Departmental Labour-Requirement or Departmental Labour -Actual for the selected project and period." /></CardContent></Card>;
  }

  return (
    <div className="space-y-6">
      <SummaryCards row={row} />
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader label="Project" sortKey="project_name" sort={sort} onSort={onSort} />
              <SortableHeader label="Duration" sortKey="duration" sort={sort} onSort={onSort} />
              <SortableHeader label="Required Labour" sortKey="planned_manpower" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Planned Labour Used" sortKey="actual_regular_manpower" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Additional Labour Used" sortKey="actual_adhoc_manpower" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Total Departmental Labour -Actual" sortKey="total_actual_manpower" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Labour Variance" sortKey="manpower_variance" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Planned Amount" sortKey="planned_amount" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Actual Amount" sortKey="actual_amount" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Amount Variance" sortKey="amount_variance" sort={sort} onSort={onSort} align="right" />
              <SortableHeader label="Variance %" sortKey="amount_variance_percentage" sort={sort} onSort={onSort} align="right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">{row.project_name}</TableCell>
              <TableCell>{emDashIfNull(row.duration)}</TableCell>
              <TableCell className="text-right">{formatNumber(row.planned_manpower)}</TableCell>
              <TableCell className="text-right">{formatNumber(row.actual_regular_manpower)}</TableCell>
              <TableCell className="text-right">{formatNumber(row.actual_adhoc_manpower)}</TableCell>
              <TableCell className="text-right">{formatNumber(row.total_actual_manpower)}</TableCell>
              <TableCell className="text-right">{labourVarianceBadge(row.manpower_variance, row.planned_manpower, row.total_actual_manpower)}</TableCell>
              <TableCell className="text-right">{formatCurrency(row.planned_amount)}</TableCell>
              <TableCell className="text-right">{formatCurrency(row.actual_amount)}</TableCell>
              <TableCell className="text-right">{amountVarianceBadge(row.amount_variance, row.planned_amount, row.actual_amount)}</TableCell>
              <TableCell className="text-right">{percentVarianceBadge(row.amount_variance, row.planned_amount, row.actual_amount, row.amount_variance_percentage)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <div className="md:hidden space-y-3">
        <div className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
          <p className="font-medium text-primary break-words">{row.project_name}</p>
          <dl className="text-sm space-y-1">
            {[
              ['Duration', emDashIfNull(row.duration)],
              ['Required Labour', formatNumber(row.planned_manpower)],
              ['Planned Labour Used', formatNumber(row.actual_regular_manpower)],
              ['Additional Labour Used', formatNumber(row.actual_adhoc_manpower)],
              ['Total Departmental Labour -Actual', formatNumber(row.total_actual_manpower)],
              ['Labour Variance', `${formatNumber(Math.abs(row.manpower_variance))} ${getLabourVarianceStatus(row.planned_manpower, row.total_actual_manpower)}`],
              ['Planned Amount', formatCurrency(row.planned_amount)],
              ['Actual Amount', formatCurrency(row.actual_amount)],
              ['Amount Variance', varianceAmountLabel(row.amount_variance, row.planned_amount, row.actual_amount)],
              ['Variance %', variancePercentLabel(row.amount_variance, row.planned_amount, row.actual_amount, row.amount_variance_percentage)],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-secondary shrink-0">{label}</dt>
                <dd className="text-primary text-right break-words">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

const CATEGORY_COLUMNS: { label: string; key: string; right?: boolean }[] = [
  { label: 'Worker Category', key: 'category_name' },
  { label: 'Skill Level', key: 'skill_level' },
  { label: 'Unit', key: 'unit' },
  { label: 'Required Labour', key: 'planned_manpower', right: true },
  { label: 'Planned Hours', key: 'planned_hours', right: true },
  { label: 'Planned Rate', key: 'planned_rate', right: true },
  { label: 'Planned Amount', key: 'planned_amount', right: true },
  { label: 'Planned Labour Used', key: 'actual_regular_manpower', right: true },
  { label: 'Additional Labour Used', key: 'actual_adhoc_manpower', right: true },
  { label: 'Total Departmental Labour -Actual', key: 'total_actual_manpower', right: true },
  { label: 'Actual Hours', key: 'actual_hours', right: true },
  { label: 'Actual Rate', key: 'actual_rate', right: true },
  { label: 'Actual Amount', key: 'actual_amount', right: true },
  { label: 'Labour Variance', key: 'manpower_variance', right: true },
  { label: 'Amount Variance', key: 'amount_variance', right: true },
];

function CategoryCard({
  query,
  sort,
  onSort,
  page,
  onPageChange,
}: {
  query: DetailedQuery<{ rows: CategoryDetailRow[]; totals: CategoryDetailTotals }>;
  sort: SortState;
  onSort: (key: string) => void;
  page: number;
  onPageChange: (p: number) => void;
}) {
  const rows = query.data?.rows ?? [];
  const totals = query.data?.totals;
  const sorted = sortRows(rows, sort);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Project Specific Category Detail</CardTitle>
        <p className="text-xs text-muted-foreground">Departmental Labour-Requirement vs Departmental Labour -Actual and cost broken down by worker category for the selected project.</p>
      </CardHeader>
      <CardContent className="p-0">
        {query.isLoading ? (
          <TableLoading rows={5} />
        ) : query.isError ? (
          <ErrorState onRetry={query.refetch} />
        ) : sorted.length === 0 ? (
          <EmptyState message="No worker categories with approved Departmental Labour-Requirement or Departmental Labour -Actual in the selected period." />
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    {CATEGORY_COLUMNS.map((c) => (
                      <SortableHeader key={c.key} label={c.label} sortKey={c.key} sort={sort} onSort={onSort} align={c.right ? 'right' : 'left'} />
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => (
                    <TableRow key={r.category_id}>
                      <TableCell className="font-medium">{r.category_name}</TableCell>
                      <TableCell>{humanize(r.skill_level)}</TableCell>
                      <TableCell>{emDashIfNull(r.unit)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.planned_manpower)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.planned_hours)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.planned_rate)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.planned_amount)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.actual_regular_manpower)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.actual_adhoc_manpower)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.total_actual_manpower)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.actual_hours)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.actual_rate)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.actual_amount)}</TableCell>
                      <TableCell className="text-right">{labourVarianceBadge(r.manpower_variance, r.planned_manpower, r.total_actual_manpower)}</TableCell>
                      <TableCell className="text-right">{amountVarianceBadge(r.amount_variance, r.planned_amount, r.actual_amount)}</TableCell>
                    </TableRow>
                  ))}
                  {totals && (
                    <TableRow className="bg-surface-secondary font-semibold">
                      <TableCell>Total</TableCell>
                      <TableCell colSpan={2} />
                      <TableCell className="text-right">{formatNumber(totals.planned_manpower)}</TableCell>
                      <TableCell colSpan={2} />
                      <TableCell className="text-right">{formatCurrency(totals.planned_amount)}</TableCell>
                      <TableCell className="text-right">{formatNumber(totals.actual_regular_manpower)}</TableCell>
                      <TableCell className="text-right">{formatNumber(totals.actual_adhoc_manpower)}</TableCell>
                      <TableCell className="text-right">{formatNumber(totals.total_actual_manpower)}</TableCell>
                      <TableCell colSpan={2} />
                      <TableCell className="text-right">{formatCurrency(totals.actual_amount)}</TableCell>
                      <TableCell className="text-right">{labourVarianceBadge(totals.manpower_variance, totals.planned_manpower, totals.total_actual_manpower)}</TableCell>
                      <TableCell className="text-right">{amountVarianceBadge(totals.amount_variance, totals.planned_amount, totals.actual_amount)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="md:hidden space-y-3 p-4">
              {pageRows.map((r) => (
                <div key={r.category_id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                  <p className="font-medium text-primary break-words">{r.category_name}</p>
                  <dl className="text-sm space-y-1">
                    {[
                      ['Skill Level', humanize(r.skill_level)],
                      ['Unit', emDashIfNull(r.unit)],
                      ['Required Labour', formatNumber(r.planned_manpower)],
                      ['Planned Hours', formatNumber(r.planned_hours)],
                      ['Planned Rate', formatNumber(r.planned_rate)],
                      ['Planned Amount', formatCurrency(r.planned_amount)],
                      ['Planned Labour Used', formatNumber(r.actual_regular_manpower)],
                      ['Additional Labour Used', formatNumber(r.actual_adhoc_manpower)],
                      ['Total Departmental Labour -Actual', formatNumber(r.total_actual_manpower)],
                      ['Actual Hours', formatNumber(r.actual_hours)],
                      ['Actual Rate', formatNumber(r.actual_rate)],
                      ['Actual Amount', formatCurrency(r.actual_amount)],
                      ['Labour Variance', `${formatNumber(Math.abs(r.manpower_variance))} ${getLabourVarianceStatus(r.planned_manpower, r.total_actual_manpower)}`],
                      ['Amount Variance', varianceAmountLabel(r.amount_variance, r.planned_amount, r.actual_amount)],
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-3">
                        <dt className="text-secondary shrink-0">{label}</dt>
                        <dd className="text-primary text-right break-words">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
              {totals && (
                <div className="rounded-lg border border-subtle bg-surface-secondary p-4 space-y-2 shadow-sm font-semibold">
                  <p className="font-medium text-primary">Total</p>
                  <dl className="text-sm space-y-1">
                    {[
                      ['Required Labour', formatNumber(totals.planned_manpower)],
                      ['Planned Amount', formatCurrency(totals.planned_amount)],
                      ['Planned Labour Used', formatNumber(totals.actual_regular_manpower)],
                      ['Additional Labour Used', formatNumber(totals.actual_adhoc_manpower)],
                      ['Total Departmental Labour -Actual', formatNumber(totals.total_actual_manpower)],
                      ['Actual Amount', formatCurrency(totals.actual_amount)],
                      ['Labour Variance', `${formatNumber(Math.abs(totals.manpower_variance))} ${getLabourVarianceStatus(totals.planned_manpower, totals.total_actual_manpower)}`],
                      ['Amount Variance', varianceAmountLabel(totals.amount_variance, totals.planned_amount, totals.actual_amount)],
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-3">
                        <dt className="text-secondary shrink-0">{label}</dt>
                        <dd className="text-primary text-right break-words">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
            <Pagination page={Math.min(page, totalPages)} pageSize={PAGE_SIZE} total={sorted.length} onPageChange={onPageChange} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

const BUDGET_COLUMNS: { label: string; key: string; right?: boolean }[] = [
  { label: 'Project', key: 'project_name' },
  { label: 'Block', key: 'block_name' },
  { label: 'Activity', key: 'activity_name' },
  { label: 'Worker Category', key: 'category_name' },
  { label: 'Required Labour', key: 'planned_manpower', right: true },
  { label: 'Total Departmental Labour -Actual', key: 'actual_manpower', right: true },
  { label: 'Labour Variance', key: 'manpower_variance', right: true },
  { label: 'Planned Amount', key: 'planned_amount', right: true },
  { label: 'Actual Amount', key: 'actual_amount', right: true },
  { label: 'Amount Variance', key: 'amount_variance', right: true },
  { label: 'Variance %', key: 'variance_percentage', right: true },
];

function BudgetCard({
  query,
  sort,
  onSort,
  page,
  onPageChange,
}: {
  query: DetailedQuery<{ rows: BudgetActualRow[] }>;
  sort: SortState;
  onSort: (key: string) => void;
  page: number;
  onPageChange: (p: number) => void;
}) {
  const rows = query.data?.rows ?? [];
  const sorted = sortRows(rows, sort);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">All Projects Budget vs Actual</CardTitle>
        <p className="text-xs text-muted-foreground">Departmental Labour-Requirement vs Departmental Labour -Actual and cost across all active projects, broken down by block, activity and worker category.</p>
      </CardHeader>
      <CardContent className="p-0">
        {query.isLoading ? (
          <TableLoading rows={5} />
        ) : query.isError ? (
          <ErrorState onRetry={query.refetch} />
        ) : sorted.length === 0 ? (
          <EmptyState message="No approved Departmental Labour-Requirement or Departmental Labour -Actual in the selected period." />
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    {BUDGET_COLUMNS.map((c) => (
                      <SortableHeader key={c.key} label={c.label} sortKey={c.key} sort={sort} onSort={onSort} align={c.right ? 'right' : 'left'} />
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell className="font-medium">{r.project_name}</TableCell>
                      <TableCell>{r.block_name}</TableCell>
                      <TableCell>{r.activity_name}</TableCell>
                      <TableCell>{r.category_name}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.planned_manpower)}</TableCell>
                      <TableCell className="text-right">{formatNumber(r.actual_manpower)}</TableCell>
                      <TableCell className="text-right">{labourVarianceBadge(r.manpower_variance, r.planned_manpower, r.actual_manpower)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.planned_amount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.actual_amount)}</TableCell>
                      <TableCell className="text-right">{amountVarianceBadge(r.amount_variance, r.planned_amount, r.actual_amount)}</TableCell>
                      <TableCell className="text-right">{percentVarianceBadge(r.amount_variance, r.planned_amount, r.actual_amount, r.variance_percentage)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="md:hidden space-y-3 p-4">
              {pageRows.map((r) => (
                <div key={r.key} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                  <p className="font-medium text-primary break-words">{r.project_name}</p>
                  <dl className="text-sm space-y-1">
                    {[
                      ['Block', r.block_name],
                      ['Activity', r.activity_name],
                      ['Worker Category', r.category_name],
                      ['Required Labour', formatNumber(r.planned_manpower)],
                      ['Total Departmental Labour -Actual', formatNumber(r.actual_manpower)],
                      ['Labour Variance', `${formatNumber(Math.abs(r.manpower_variance))} ${getLabourVarianceStatus(r.planned_manpower, r.actual_manpower)}`],
                      ['Planned Amount', formatCurrency(r.planned_amount)],
                      ['Actual Amount', formatCurrency(r.actual_amount)],
                      ['Amount Variance', varianceAmountLabel(r.amount_variance, r.planned_amount, r.actual_amount)],
                      ['Variance %', variancePercentLabel(r.amount_variance, r.planned_amount, r.actual_amount, r.variance_percentage)],
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-3">
                        <dt className="text-secondary shrink-0">{label}</dt>
                        <dd className="text-primary text-right break-words">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
            <Pagination page={Math.min(page, totalPages)} pageSize={PAGE_SIZE} total={sorted.length} onPageChange={onPageChange} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

const MULTI_COLUMNS: { label: string; key: string; right?: boolean }[] = [
  { label: 'Project', key: 'project_name' },
  { label: 'Required Labour', key: 'planned_manpower', right: true },
  { label: 'Planned Labour Used', key: 'actual_regular_manpower', right: true },
  { label: 'Additional Labour Used', key: 'actual_adhoc_manpower', right: true },
  { label: 'Total Departmental Labour -Actual', key: 'total_actual_manpower', right: true },
  { label: 'Labour Variance', key: 'manpower_variance', right: true },
  { label: 'Planned Amount', key: 'planned_amount', right: true },
  { label: 'Actual Amount', key: 'actual_amount', right: true },
  { label: 'Amount Variance', key: 'amount_variance', right: true },
  { label: 'Variance %', key: 'amount_variance_percentage', right: true },
];

function MultiCard({
  query,
  sort,
  onSort,
  page,
  onPageChange,
  selectedProjectIds,
}: {
  query: DetailedQuery<{ rows: MultiSummaryRow[]; totals: MultiSummaryTotals }>;
  sort: SortState;
  onSort: (key: string) => void;
  page: number;
  onPageChange: (p: number) => void;
  selectedProjectIds: string[];
}) {
  const rows = query.data?.rows ?? [];
  const totals = query.data?.totals;
  const sorted = sortRows(rows, sort);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const chartData = rows.map((r) => ({
    name: r.project_name,
    'Required Labour': r.planned_manpower,
    'Total Actual Labour': r.total_actual_manpower,
  }));

  const totalRequired = totals?.planned_manpower ?? 0;
  const totalPlannedUsed = totals?.actual_regular_manpower ?? 0;
  const totalAdditionalUsed = totals?.actual_adhoc_manpower ?? 0;
  const totalActual = totals?.total_actual_manpower ?? 0;
  const totalLabVar = totals?.manpower_variance ?? 0;
  const totalPlannedAmt = totals?.planned_amount ?? 0;
  const totalActualAmt = totals?.actual_amount ?? 0;
  const totalAmtVar = totals?.amount_variance ?? 0;
  const totalVarPct = totals?.amount_variance_percentage ?? null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Required Labour', value: formatNumber(totalRequired), color: 'text-primary' },
          { label: 'Planned Labour Used', value: formatNumber(totalPlannedUsed), color: 'text-primary' },
          { label: 'Additional Labour Used', value: formatNumber(totalAdditionalUsed), color: 'text-primary' },
          { label: 'Total Departmental Labour -Actual', value: formatNumber(totalActual), color: 'text-primary' },
          {
            label: 'Labour Variance',
            value: totalLabVar === 0 ? 'On Plan' : `${Math.abs(totalLabVar)} ${totalLabVar < 0 ? 'Under Plan' : 'Over Plan'}`,
            color: totalLabVar === 0 ? 'text-blue-600' : totalLabVar < 0 ? 'text-emerald-600' : 'text-red-600',
          },
        ].map((c) => (
          <Card key={c.label}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground truncate">{c.label}</p>
              <p className={`text-lg font-semibold mt-1 ${c.color}`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <LabourComparisonChart data={rows} chartData={chartData} />
        </div>
        <AmountSummaryCard
          plannedAmount={totalPlannedAmt}
          actualAmount={totalActualAmt}
          amountVariance={totalAmtVar}
          variancePercentage={totalVarPct}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Multi Project Summary</CardTitle>
          <p className="text-xs text-muted-foreground">One row per selected active project that has approved planned or actual labour in the selected period.</p>
          {query.data && (
            <p className="text-xs text-muted-foreground">
              Showing {rows.length} of {selectedProjectIds.length} selected {selectedProjectIds.length === 1 ? 'project' : 'projects'}
            </p>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {query.isLoading ? (
            <TableLoading rows={5} />
          ) : query.isError ? (
            <ErrorState onRetry={query.refetch} />
          ) : sorted.length === 0 ? (
            <EmptyState message="No projects have approved Departmental Labour-Requirement or Departmental Labour -Actual in the selected period." />
          ) : (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {MULTI_COLUMNS.map((c) => (
                        <SortableHeader key={c.key} label={c.label} sortKey={c.key} sort={sort} onSort={onSort} align={c.right ? 'right' : 'left'} />
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageRows.map((r) => (
                      <TableRow key={r.project_id}>
                        <TableCell className="font-medium">{r.project_name}</TableCell>
                        <TableCell className="text-right">{formatNumber(r.planned_manpower)}</TableCell>
                        <TableCell className="text-right">{formatNumber(r.actual_regular_manpower)}</TableCell>
                        <TableCell className="text-right">{formatNumber(r.actual_adhoc_manpower)}</TableCell>
                        <TableCell className="text-right">{formatNumber(r.total_actual_manpower)}</TableCell>
                        <TableCell className="text-right">{labourVarianceBadge(r.manpower_variance, r.planned_manpower, r.total_actual_manpower)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.planned_amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.actual_amount)}</TableCell>
                        <TableCell className="text-right">{amountVarianceBadge(r.amount_variance, r.planned_amount, r.actual_amount)}</TableCell>
                        <TableCell className="text-right">{percentVarianceBadge(r.amount_variance, r.planned_amount, r.actual_amount, r.amount_variance_percentage)}</TableCell>
                      </TableRow>
                    ))}
                    {totals && (
                      <TableRow className="bg-surface-secondary font-semibold">
                        <TableCell>Total</TableCell>
                        <TableCell className="text-right">{formatNumber(totals.planned_manpower)}</TableCell>
                        <TableCell className="text-right">{formatNumber(totals.actual_regular_manpower)}</TableCell>
                        <TableCell className="text-right">{formatNumber(totals.actual_adhoc_manpower)}</TableCell>
                        <TableCell className="text-right">{formatNumber(totals.total_actual_manpower)}</TableCell>
                        <TableCell className="text-right">{labourVarianceBadge(totals.manpower_variance, totals.planned_manpower, totals.total_actual_manpower)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(totals.planned_amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(totals.actual_amount)}</TableCell>
                        <TableCell className="text-right">{amountVarianceBadge(totals.amount_variance, totals.planned_amount, totals.actual_amount)}</TableCell>
                        <TableCell className="text-right">{percentVarianceBadge(totals.amount_variance, totals.planned_amount, totals.actual_amount, totals.amount_variance_percentage)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="md:hidden space-y-3 p-4">
                {pageRows.map((r) => (
                  <div key={r.project_id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                    <p className="font-medium text-primary break-words">{r.project_name}</p>
                    <dl className="text-sm space-y-1">
                      {[
                        ['Required Labour', formatNumber(r.planned_manpower)],
                        ['Planned Labour Used', formatNumber(r.actual_regular_manpower)],
                        ['Additional Labour Used', formatNumber(r.actual_adhoc_manpower)],
                        ['Total Departmental Labour -Actual', formatNumber(r.total_actual_manpower)],
                        ['Labour Variance', `${formatNumber(Math.abs(r.manpower_variance))} ${getLabourVarianceStatus(r.planned_manpower, r.total_actual_manpower)}`],
                        ['Planned Amount', formatCurrency(r.planned_amount)],
                        ['Actual Amount', formatCurrency(r.actual_amount)],
                        ['Amount Variance', varianceAmountLabel(r.amount_variance, r.planned_amount, r.actual_amount)],
                        ['Variance %', variancePercentLabel(r.amount_variance, r.planned_amount, r.actual_amount, r.amount_variance_percentage)],
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-3">
                          <dt className="text-secondary shrink-0">{label}</dt>
                          <dd className="text-primary text-right break-words">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
                {totals && (
                  <div className="rounded-lg border border-subtle bg-surface-secondary p-4 space-y-2 shadow-sm font-semibold">
                    <p className="font-medium text-primary">Total</p>
                    <dl className="text-sm space-y-1">
                      {[
                        ['Required Labour', formatNumber(totals.planned_manpower)],
                        ['Planned Labour Used', formatNumber(totals.actual_regular_manpower)],
                        ['Additional Labour Used', formatNumber(totals.actual_adhoc_manpower)],
                        ['Total Departmental Labour -Actual', formatNumber(totals.total_actual_manpower)],
                        ['Labour Variance', `${formatNumber(Math.abs(totals.manpower_variance))} ${getLabourVarianceStatus(totals.planned_manpower, totals.total_actual_manpower)}`],
                        ['Planned Amount', formatCurrency(totals.planned_amount)],
                        ['Actual Amount', formatCurrency(totals.actual_amount)],
                        ['Amount Variance', varianceAmountLabel(totals.amount_variance, totals.planned_amount, totals.actual_amount)],
                        ['Variance %', variancePercentLabel(totals.amount_variance, totals.planned_amount, totals.actual_amount, totals.amount_variance_percentage)],
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-3">
                          <dt className="text-secondary shrink-0">{label}</dt>
                          <dd className="text-primary text-right break-words">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
              <Pagination page={Math.min(page, totalPages)} pageSize={PAGE_SIZE} total={sorted.length} onPageChange={onPageChange} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


